import React, { useState, useEffect } from 'react';
import type { AxiosError } from 'axios';
import {
  Modal, Tabs, TextInput, Textarea, Button, Group, Stack, Text, Switch,
  Avatar, FileButton, Badge, Table, ActionIcon, Paper, Alert, CopyButton, Tooltip,
  SegmentedControl, Box, PasswordInput,
} from '@mantine/core';
import {
  User, Key, ShieldCheck, Buildings, UploadSimple, Copy, Check,
  Trash, Plus, CheckCircle, PaintBrush, Sun, Moon, Desktop,
} from '@phosphor-icons/react';
import { useMantineColorScheme } from '@mantine/core';
import { useAuthStore } from '../../store/auth.store';
import {
  useDeveloperProfile,
  useUpdateDeveloperProfile,
  useUploadAvatar,
  useChangeDeveloperPassword,
  useSetDeveloperTwoFactor,
} from '../../hooks/developer/useDeveloperProfile';
import { useAuthThrottle } from '../../hooks/useAuthThrottle';
import { validateNewPassword, isStrongPassword } from '../../utils/passwordPolicy';
import { parseApiError } from '../../utils/apiError';
import { PasswordPolicyChecklist, ThrottleAlert, ErrorAlert } from '../auth/AuthFeedback';
import { toast } from '../../utils/toast';
import {
  useDeveloperTokens,
  useCreateDeveloperToken,
  useRevokeDeveloperToken,
} from '../../hooks/developer/useDeveloperTokens';
import {
  useDeveloperProvider,
  useUpdateDeveloperProvider,
} from '../../hooks/developer/useDeveloperProvider';
import { formatDate } from '../../utils/formatters';

interface DeveloperSettingsModalProps {
  opened: boolean;
  onClose: () => void;
  initialTab?: string;
}

export function DeveloperSettingsModal({ opened, onClose, initialTab = 'profile' }: DeveloperSettingsModalProps) {
  const { user } = useAuthStore();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  // Developer Queries & Mutations
  const { data: profile } = useDeveloperProfile();
  const updateProfileMutation = useUpdateDeveloperProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const changeDevPasswordMutation = useChangeDeveloperPassword();
  const setTwoFactorMutation = useSetDeveloperTwoFactor();
  const passwordThrottle = useAuthThrottle();
  const twoFactorThrottle = useAuthThrottle();

  const { data: tokens, isLoading: tokensLoading } = useDeveloperTokens();
  const createTokenMutation = useCreateDeveloperToken();
  const revokeTokenMutation = useRevokeDeveloperToken();

  const { data: provider } = useDeveloperProvider();
  const updateProviderMutation = useUpdateDeveloperProvider();

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [publicEmail, setPublicEmail] = useState('');
  const [birthday, setBirthday] = useState('');

  // 2FA toggle: the switch only changes after the API (with current password) succeeds
  const [pendingTwoFactor, setPendingTwoFactor] = useState<boolean | null>(null);
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Provider edit state
  const [providerName, setProviderName] = useState('');
  const [providerDesc, setProviderDesc] = useState('');

  // Created Token Display
  const [createdTokenSecret, setCreatedTokenSecret] = useState<string | null>(null);
  const [tokenLabel, setTokenLabel] = useState('');
  const [tokenLabelError, setTokenLabelError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      setBio(profile.bio || '');
      setPublicEmail(profile.publicEmail || '');
      setBirthday(profile.birthday || '');
    }
  }, [profile]);

  useEffect(() => {
    if (provider) {
      setProviderName(provider.name || '');
      setProviderDesc(provider.description || '');
    }
  }, [provider]);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({
      displayName,
      bio,
      publicEmail,
      birthday: birthday || undefined,
    });
  };

  const closeTwoFactorDialog = () => {
    setPendingTwoFactor(null);
    setTwoFactorPassword('');
    setTwoFactorError(null);
  };

  const handleConfirmTwoFactor = (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingTwoFactor === null) return;
    if (!twoFactorPassword) {
      setTwoFactorError('Please enter your current password.');
      return;
    }
    setTwoFactorError(null);
    setTwoFactorMutation.mutate(
      { enabled: pendingTwoFactor, currentPassword: twoFactorPassword },
      {
        onSuccess: () => {
          toast.success(pendingTwoFactor ? 'Two-factor authentication enabled' : 'Two-factor authentication disabled');
          closeTwoFactorDialog();
        },
        onError: (err) => {
          if (!twoFactorThrottle.handleError(err)) {
            setTwoFactorError(parseApiError(err, 'Failed to update two-factor authentication').message);
          }
        },
      },
    );
  };

  const handleAvatarUpload = (file: File | null) => {
    if (!file) return;
    uploadAvatarMutation.mutate(file);
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    const validationError = validateNewPassword({ currentPassword, newPassword, confirmPassword });
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    // Other sessions are signed out; this session receives fresh cookies in the response.
    changeDevPasswordMutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setPasswordSuccess(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
        onError: (err) => {
          if (!passwordThrottle.handleError(err)) {
            setPasswordError(parseApiError(err, 'Failed to change password').message);
          }
        },
      }
    );
  };

  const handleCreateToken = (e: React.FormEvent) => {
    e.preventDefault();
    setTokenLabelError(null);

    const trimmed = tokenLabel.trim();
    if (!trimmed) {
      setTokenLabelError('Vui lòng nhập tên token.');
      return;
    }
    if (trimmed.length > 100) {
      setTokenLabelError('Tên token tối đa 100 ký tự.');
      return;
    }

    createTokenMutation.mutate(
      { label: trimmed },
      {
        onSuccess: (data) => {
          if (data.token) {
            setCreatedTokenSecret(data.token);
          }
          setTokenLabel('');
          setTokenLabelError(null);
        },
        onError: (err: AxiosError<{ message?: string | string[] }>) => {
          const msg = err.response?.data?.message;
          setTokenLabelError(Array.isArray(msg) ? msg.join(', ') : msg || err.message || 'Không thể tạo token.');
        },
      }
    );
  };

  const handleSaveProvider = (e: React.FormEvent) => {
    e.preventDefault();
    updateProviderMutation.mutate({
      name: providerName,
      description: providerDesc,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={8}>
          <Text fw={600} size="sm" c="var(--text-primary)">Developer Settings & Tokens</Text>
          <Badge size="xs" color="gray" variant="outline">{user?.email}</Badge>
        </Group>
      }
      size="lg"
      centered
    >
      <Tabs defaultValue={initialTab} color="orange" styles={{
        list: { borderColor: 'var(--boundary)', marginBottom: 16 },
        tab: { color: 'var(--text-secondary)', '&[data-active]': { color: 'var(--amber)', borderColor: 'var(--amber)' } },
      }}>
        <Tabs.List>
          <Tabs.Tab value="profile" leftSection={<User size={14} />}>Profile</Tabs.Tab>
          <Tabs.Tab value="appearance" leftSection={<PaintBrush size={14} />}>Appearance</Tabs.Tab>
          <Tabs.Tab value="password" leftSection={<Key size={14} />}>Password</Tabs.Tab>
          <Tabs.Tab value="tokens" leftSection={<ShieldCheck size={14} />}>API Tokens</Tabs.Tab>
          <Tabs.Tab value="provider" leftSection={<Buildings size={14} />}>My Provider</Tabs.Tab>
        </Tabs.List>

        {/* ── Tab: Profile ── */}
        <Tabs.Panel value="profile">
          <form onSubmit={handleSaveProfile}>
            <Stack gap={16}>
              <Group gap={16} align="center">
                <Avatar src={profile?.avatarUrl} size={64} radius="xl" color="orange">
                  {displayName?.[0] || 'D'}
                </Avatar>
                <div>
                  <Text size="sm" fw={600} c="var(--text-primary)">Developer Avatar</Text>
                  <Text size="xs" c="var(--text-secondary)" mb={8}>Supported formats: PNG, JPEG, WebP (max 2MB)</Text>
                  <FileButton onChange={handleAvatarUpload} accept="image/png,image/jpeg,image/webp">
                    {(props) => (
                      <Button
                        {...props}
                        size="xs"
                        variant="outline"
                        leftSection={<UploadSimple size={12} />}
                        loading={uploadAvatarMutation.isPending}
                        style={{ borderColor: 'var(--boundary)', color: 'var(--text-primary)' }}
                      >
                        Change Avatar
                      </Button>
                    )}
                  </FileButton>
                </div>
              </Group>

              <TextInput
                label="Display Name"
                placeholder="Your Public Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              <TextInput
                label="Public Contact Email"
                placeholder="dev@example.com"
                value={publicEmail}
                onChange={(e) => setPublicEmail(e.target.value)}
              />

              <TextInput
                label="Birthday"
                placeholder="YYYY-MM-DD"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
              />

              <Textarea
                label="Bio"
                placeholder="A brief developer bio..."
                minRows={2}
                maxRows={4}
                autosize
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />

              <Paper p="sm" style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6 }}>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500} c="var(--text-primary)">Two-Factor Authentication (2FA)</Text>
                    <Text size="xs" c="var(--text-secondary)">Enforce OTP login verification for your developer account</Text>
                  </div>
                  <Switch
                    checked={!!profile?.twoFactorEnabled}
                    onChange={(e) => setPendingTwoFactor(e.currentTarget.checked)}
                    disabled={!profile}
                    color="orange"
                  />
                </Group>
              </Paper>

              <Group justify="flex-end" mt={8}>
                <Button
                  type="submit"
                  loading={updateProfileMutation.isPending}
                  style={{ background: 'var(--amber)', color: '#FFFFFF', borderRadius: 4, fontWeight: 600 }}
                >
                  Save Profile
                </Button>
              </Group>
            </Stack>
          </form>
        </Tabs.Panel>

        {/* ── Tab: Appearance ── */}
        <Tabs.Panel value="appearance">
          <Stack gap={16}>
            <div style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6, padding: 16 }}>
              <Text size="sm" fw={600} c="var(--text-primary)" mb={4}>
                Theme Preference
              </Text>
              <Text size="xs" c="var(--text-secondary)" mb={16}>
                Choose between Light, Dark, or System mode for your CMS interface.
              </Text>

              <Box>
                <SegmentedControl
                  fullWidth
                  value={colorScheme}
                  onChange={(value) => setColorScheme(value as any)}
                  data={[
                    {
                      value: 'dark',
                      label: (
                        <Group gap={6} justify="center">
                          <Moon size={16} weight="duotone" />
                          <span>Dark Mode</span>
                        </Group>
                      ),
                    },
                    {
                      value: 'light',
                      label: (
                        <Group gap={6} justify="center">
                          <Sun size={16} weight="duotone" />
                          <span>Light Mode</span>
                        </Group>
                      ),
                    },
                    {
                      value: 'auto',
                      label: (
                        <Group gap={6} justify="center">
                          <Desktop size={16} weight="duotone" />
                          <span>System Auto</span>
                        </Group>
                      ),
                    },
                  ]}
                  styles={{
                    root: { background: 'var(--surface)', border: '1px solid var(--boundary)', padding: 4, borderRadius: 6 },
                    indicator: { background: 'var(--surface-hi)' },
                    label: { color: 'var(--text-primary)', fontWeight: 500, fontSize: 13 },
                  }}
                />
              </Box>
            </div>
          </Stack>
        </Tabs.Panel>

        {/* ── Tab: Password ── */}
        <Tabs.Panel value="password">
          <form onSubmit={handleChangePassword}>
            <Stack gap={16}>
              {passwordSuccess && (
                <Alert icon={<CheckCircle size={16} />} color="teal" styles={{ root: { background: 'var(--green-dim)', borderColor: 'var(--green-border)', color: 'var(--green)' } }}>
                  Password updated successfully.
                </Alert>
              )}

              <ThrottleAlert throttle={passwordThrottle} />
              <ErrorAlert message={passwordError} />

              <TextInput
                type="password"
                label="Current Password"
                placeholder="Enter current password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />

              <TextInput
                type="password"
                label="New Password"
                placeholder="At least 12 characters"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              {newPassword && <PasswordPolicyChecklist password={newPassword} />}

              <TextInput
                type="password"
                label="Confirm New Password"
                placeholder="Re-enter new password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <Group justify="flex-end" mt={8}>
                <Button
                  type="submit"
                  loading={changeDevPasswordMutation.isPending}
                  disabled={passwordThrottle.isLocked || !isStrongPassword(newPassword)}
                  color="amber"
                  style={{ borderRadius: 4, fontWeight: 600 }}
                >
                  Change Password
                </Button>
              </Group>
            </Stack>
          </form>
        </Tabs.Panel>

        {/* ── Tab: API Tokens ── */}
        <Tabs.Panel value="tokens">
          <Stack gap={16}>
            <div>
              <Text size="sm" fw={600} c="var(--text-primary)">Personal API Tokens</Text>
              <Text size="xs" c="var(--text-secondary)">Use API Tokens to automate MiniApp bundle uploads and CI/CD pipelines</Text>
            </div>
            <form onSubmit={handleCreateToken} noValidate>
              <Group align="flex-start" gap={8} wrap="nowrap">
                <TextInput
                  label="Tên token"
                  placeholder="Ví dụ: CI/CD pipeline"
                  required
                  maxLength={100}
                  size="xs"
                  style={{ flex: 1 }}
                  value={tokenLabel}
                  error={tokenLabelError}
                  onChange={(e) => {
                    setTokenLabel(e.currentTarget.value);
                    setTokenLabelError(null);
                  }}
                />
                <Button
                  type="submit"
                  size="xs"
                  mt={22}
                  leftSection={<Plus size={12} weight="bold" />}
                  loading={createTokenMutation.isPending}
                  style={{ background: 'var(--amber)', color: '#FFFFFF', borderRadius: 4, fontWeight: 600 }}
                >
                  Generate Token
                </Button>
              </Group>
            </form>

            {createdTokenSecret && (
              <Alert
                icon={<Key size={16} />}
                color="orange"
                styles={{ root: { background: 'var(--amber-dim)', borderColor: 'var(--amber)', color: 'var(--text-primary)' } }}
              >
                <Text size="xs" fw={700} c="var(--amber)" mb={4}>
                  Copy your new API token now. You will not be able to view it again!
                </Text>
                <Group gap={8} mt={6}>
                  <Text size="xs" style={{ fontFamily: 'Geist Mono', background: 'var(--bg-root)', padding: '6px 10px', borderRadius: 4, flex: 1, color: 'var(--text-primary)' }}>
                    {createdTokenSecret}
                  </Text>
                  <CopyButton value={createdTokenSecret}>
                    {({ copied, copy }) => (
                      <Button size="xs" onClick={copy} color={copied ? 'teal' : 'gray'} style={{ borderRadius: 4 }}>
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Alert>
            )}

            <div style={{ background: 'var(--surface)', border: '1px solid var(--boundary)', borderRadius: 6, overflow: 'hidden' }}>
              <Table verticalSpacing="xs">
                <Table.Thead style={{ background: 'var(--bg-root)' }}>
                  <Table.Tr>
                    <Table.Th style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Label / ID</Table.Th>
                    <Table.Th style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Created</Table.Th>
                    <Table.Th style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Last Used</Table.Th>
                    <Table.Th style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Status</Table.Th>
                    <Table.Th style={{ width: 40 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tokensLoading ? (
                    <Table.Tr><Table.Td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Loading tokens...</Table.Td></Table.Tr>
                  ) : !tokens || tokens.length === 0 ? (
                    <Table.Tr><Table.Td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 20 }}>No API tokens generated yet.</Table.Td></Table.Tr>
                  ) : (
                    tokens.map((tok) => (
                      <Table.Tr key={tok.id}>
                        <Table.Td>
                          <Text size="xs" fw={600} c="var(--text-primary)">{tok.label || 'Default Token'}</Text>
                          <Text size="xs" c="var(--text-secondary)" style={{ fontFamily: 'Geist Mono' }}>{tok.id}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="var(--text-secondary)" style={{ fontFamily: 'Geist Mono' }}>{formatDate(tok.createdAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="var(--text-secondary)" style={{ fontFamily: 'Geist Mono' }}>
                            {tok.lastUsedAt ? formatDate(tok.lastUsedAt) : 'Never'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {tok.revokedAt ? (
                            <Badge size="xs" color="red" variant="outline">Revoked</Badge>
                          ) : (
                            <Badge size="xs" color="teal" variant="outline">Active</Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {!tok.revokedAt && (
                            <Tooltip label="Revoke Token">
                              <ActionIcon
                                size="sm"
                                color="red"
                                variant="subtle"
                                loading={revokeTokenMutation.isPending}
                                onClick={() => revokeTokenMutation.mutate(tok.id)}
                              >
                                <Trash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </div>
          </Stack>
        </Tabs.Panel>

        {/* ── Tab: Provider ── */}
        <Tabs.Panel value="provider">
          <form onSubmit={handleSaveProvider}>
            <Stack gap={16}>
              <div style={{ background: 'var(--bg-input)', border: '1px solid var(--boundary)', borderRadius: 6, padding: 16 }}>
                <Text size="xs" fw={600} c="var(--text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Provider Organization
                </Text>
                <Text size="sm" fw={600} c="var(--text-primary)" mt={4}>{provider?.name || 'My Organization'}</Text>
                <Text size="xs" c="var(--text-secondary)" style={{ fontFamily: 'Geist Mono' }}>ID: {provider?.id || 'N/A'}</Text>
              </div>

              <TextInput
                label="Provider Name"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
              />

              <Textarea
                label="Description"
                minRows={2}
                maxRows={4}
                autosize
                value={providerDesc}
                onChange={(e) => setProviderDesc(e.target.value)}
              />

              <Group justify="flex-end" mt={8}>
                <Button
                  type="submit"
                  loading={updateProviderMutation.isPending}
                  style={{ background: 'var(--amber)', color: '#FFFFFF', borderRadius: 4, fontWeight: 600 }}
                >
                  Save Provider Details
                </Button>
              </Group>
            </Stack>
          </form>
        </Tabs.Panel>
      </Tabs>

      {/* ── 2FA toggle confirmation (requires current password) ── */}
      <Modal
        opened={pendingTwoFactor !== null}
        onClose={closeTwoFactorDialog}
        title={
          <Text fw={600} size="sm" c="var(--text-primary)">
            {pendingTwoFactor ? 'Enable' : 'Disable'} Two-Factor Authentication
          </Text>
        }
        size="sm"
        centered
      >
        <form onSubmit={handleConfirmTwoFactor}>
          <Stack gap={14}>
            <Text size="xs" c="var(--text-secondary)">Confirm with your current password to continue.</Text>
            <ThrottleAlert throttle={twoFactorThrottle} />
            <ErrorAlert message={twoFactorError} />
            <PasswordInput
              label="Current Password"
              required
              autoFocus
              autoComplete="current-password"
              value={twoFactorPassword}
              onChange={(e) => setTwoFactorPassword(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={closeTwoFactorDialog} style={{ color: 'var(--text-secondary)' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="amber"
                loading={setTwoFactorMutation.isPending}
                disabled={twoFactorThrottle.isLocked}
                style={{ fontWeight: 600 }}
              >
                Confirm
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Modal>
  );
}
